import React, { FC, useEffect, useState } from "react"
import { observer } from "mobx-react"
import { useStore } from "stores/utils/use-store"
import { Table, Card, Button, Space, Switch, InputNumber, Modal } from "antd"
import { gameNames } from "abstract/constants/game-ids"
import { CreateEditGameModeMultiplierModal } from "./create-edit-game-mode-multiplier-modal"
import { GameModeMultiplier, BulkUpsertGameModeMultipliersDto } from "../store/types"
import { showDeleteConfirm, GameAutoComplete } from "components"
import { notifications } from "notifications"

export const GameModeMultipliersTab: FC = observer(() => {
  const { dataStore: { gameModeMultipliersStore } } = useStore()
  const [gameInputValue, setGameInputValue] = useState<string>("")
  const [showModal, setShowModal] = useState(false)
  const [editingMultiplier, setEditingMultiplier] = useState<GameModeMultiplier | null>(null)
  const [bulkEditMode, setBulkEditMode] = useState(false)
  const [editedMultipliers, setEditedMultipliers] = useState<Record<string, number>>({})
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false)

  useEffect(() => {
    gameModeMultipliersStore.getAllGameModeMultipliers()
  }, [gameModeMultipliersStore])

  useEffect(() => {
    if (!gameModeMultipliersStore.currentGameId) {
      setBulkEditMode(false)
      setEditedMultipliers({})
      setHasUnsavedChanges(false)
    }
  }, [gameModeMultipliersStore.currentGameId])

  const handleGameChange = (value: string) => {
    setGameInputValue(value)
  }

  const handleGameApply = (value: string, gameId: string | null) => {
    if (gameId) {
      gameModeMultipliersStore.getGameModeMultipliersByGame(gameId)
    } else if (!value) {
      handleGameClear()
    }
  }

  const handleGameClear = () => {
    setGameInputValue("")
    gameModeMultipliersStore.clearGameFilter()
    gameModeMultipliersStore.getAllGameModeMultipliers()
  }

  const handleDelete = (multiplier: GameModeMultiplier) => {
    showDeleteConfirm(
      `game mode multiplier ${multiplier.gameMode} for game ${multiplier.gameId}`,
      undefined,
      async() => {
        try {
          await gameModeMultipliersStore.deleteGameModeMultiplier(multiplier.id)
          notifications.success("Game mode multiplier deleted")
        } catch (error: any) {
          notifications.error(error?.cause?.message || error?.message || "Failed to delete game mode multiplier")
        }
      },
    )
  }

  const handleBulkEditToggle = (checked: boolean) => {
    if (!checked && hasUnsavedChanges) {
      Modal.confirm({
        title: "Discard unsaved changes?",
        content: "You have unsaved changes. Are you sure you want to discard them?",
        okText: "Discard Changes",
        cancelText: "Cancel",
        onOk: () => {
          setBulkEditMode(false)
          setEditedMultipliers({})
          setHasUnsavedChanges(false)
        },
      })

      return
    }

    if (checked) {
      const initialValues: Record<string, number> = {}
      gameModeMultipliersStore.multipliers.forEach(multiplier => {
        initialValues[multiplier.id] = multiplier.multiplier
      })
      setEditedMultipliers(initialValues)
      setHasUnsavedChanges(false)
    } else {
      setEditedMultipliers({})
      setHasUnsavedChanges(false)
    }

    setBulkEditMode(checked)
  }

  const handleMultiplierChange = (id: string, value: number | null) => {
    if (value === null) return

    setEditedMultipliers(prev => ({
      ...prev,
      [id]: value,
    }))
    setHasUnsavedChanges(true)
  }

  const handleBulkUpsert = async() => {
    try {
      const multipliers = gameModeMultipliersStore.multipliers
      if (multipliers.length === 0) return

      const gameId = multipliers[0].gameId
      const data: BulkUpsertGameModeMultipliersDto = {
        gameId,
        multipliers: multipliers.map(multiplier => ({
          gameMode: multiplier.gameMode,
          multiplier: editedMultipliers[multiplier.id] ?? multiplier.multiplier,
        })),
      }

      await gameModeMultipliersStore.bulkUpsertGameModeMultipliers(data)
      notifications.success("Multipliers updated successfully")
      setBulkEditMode(false)
      setEditedMultipliers({})
      setHasUnsavedChanges(false)
    } catch (error: any) {
      notifications.error(error?.cause?.message || error?.message || "Failed to update multipliers")
    }
  }

  const getgameModeParameter = (record: GameModeMultiplier): string => {
    return record.gameModeParameter || "gameMode"
  }

  const columns = [
    {
      title: "Game ID",
      key: "gameId",
      dataIndex: "gameId",
      width: 200,
      render: (value: number) => {
        if (!value) return "-"

        const gameName = gameNames[value]

        return gameName ? `${value} - ${gameName}` : value
      },
    },
    {
      title: "Gamemode Parameter",
      key: "gameModeParameter",
      dataIndex: "gameModeParameter",
      width: 200,
      render: (_: any, record: GameModeMultiplier) => {
        return getgameModeParameter(record)
      },
    },
    {
      title: "Game Mode",
      key: "gameMode",
      dataIndex: "gameMode",
      width: 200,
    },
    {
      title: "Multiplier",
      key: "multiplier",
      dataIndex: "multiplier",
      width: 150,
      render: (value: number | string | null | undefined, record: GameModeMultiplier) => {
        if (bulkEditMode) {
          return (
            <InputNumber
              min={0.1}
              step={0.1}
              value={editedMultipliers[record.id] ?? value}
              onChange={val => handleMultiplierChange(record.id, val)}
              style={{ width: "100%" }}
              onClick={e => e.stopPropagation()}
            />
          )
        }

        const numValue = typeof value === "number" ? value : Number(value)

        return isNaN(numValue) ? "-" : numValue.toFixed(2)
      },
    },
    {
      title: "Actions",
      key: "actions",
      width: 150,
      fixed: "right" as const,
      render: (_: any, record: GameModeMultiplier) => (
        <Button
          type="link"
          danger
          onClick={e => {
            e.stopPropagation()
            handleDelete(record)
          }}
        >
          Delete
        </Button>
      ),
    },
  ]

  return (
    <div>
      <Space direction="vertical" size="large" style={{ width: "100%" }}>
        <div>
          <Button
            type="primary"
            onClick={() => {
              setEditingMultiplier(null)
              setShowModal(true)
            }}
            style={{ marginBottom: 16 }}
          >
            Create Game Mode Multiplier
          </Button>
          <Card title="Game Mode Multipliers" size="small">
            <Space direction="vertical" style={{ width: "100%" }} size="middle">
              <Space wrap>
                <GameAutoComplete
                  value={gameInputValue}
                  onChange={handleGameChange}
                  onApply={handleGameApply}
                  onClear={handleGameClear}
                  applyOnBlur={true}
                />
                <Button
                  type="primary"
                  onClick={() => {
                    const gameId = gameInputValue.includes(" - ")
                      ? gameInputValue.split(" - ")[0]
                      : gameInputValue.trim()
                    handleGameApply(gameInputValue, gameId || null)
                  }}
                >
                  Filter
                </Button>
                {gameModeMultipliersStore.currentGameId && (
                  <>
                    <Button
                      onClick={handleGameClear}
                    >
                      Clear Filter
                    </Button>
                    <Space>
                      <span>Bulk Edit Mode:</span>
                      <Switch
                        checked={bulkEditMode}
                        onChange={handleBulkEditToggle}
                      />
                    </Space>
                    {bulkEditMode && (
                      <Button
                        type="primary"
                        onClick={handleBulkUpsert}
                        loading={gameModeMultipliersStore.isLoading}
                      >
                        Upsert
                      </Button>
                    )}
                  </>
                )}
              </Space>
              <Table
                dataSource={Array.isArray(gameModeMultipliersStore.multipliers) ? gameModeMultipliersStore.multipliers : []}
                columns={columns}
                rowKey="id"
                loading={gameModeMultipliersStore.isLoading}
                pagination={{ pageSize: 20 }}
                scroll={{ x: "max-content" }}
                onRow={record => ({
                  style: { cursor: bulkEditMode ? "default" : "pointer" },
                  onClick: () => {
                    if (!bulkEditMode) {
                      setEditingMultiplier(record)
                      setShowModal(true)
                    }
                  },
                })}
              />
            </Space>
          </Card>
        </div>
      </Space>
      <CreateEditGameModeMultiplierModal
        visible={showModal}
        onClose={() => {
          setShowModal(false)
          setEditingMultiplier(null)
        }}
        initialData={editingMultiplier}
      />
    </div>
  )
})
